import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

type Values = Record<string, any>;

function drawWrappedText(opts: {
  page: any;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  lineHeight: number;
  font: any;
  size: number;
}) {
  const { page, text, x, y, maxWidth, lineHeight, font, size } = opts;
  const paragraphs = String(text).split(/\n/);
  let cy = y;
  for (let p = 0; p < paragraphs.length; p++) {
    const words = paragraphs[p].split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = "";
    for (const w of words) {
      const test = current ? `${current} ${w}` : w;
      const width = font.widthOfTextAtSize(test, size);
      if (width > maxWidth && current) {
        lines.push(current);
        current = w;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    for (const line of lines) {
      page.drawText(line, { x, y: cy, size, font, color: rgb(0.15, 0.15, 0.15) });
      cy -= lineHeight;
    }
    if (p < paragraphs.length - 1) cy -= 2; // small gap between paragraphs
  }
  return cy;
}

function formatLocalDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

export async function generateHipaaPdf(values: Values, signatureDataUrl?: string): Promise<Blob> {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([612, 792]); // US Letter portrait
  const { height, width } = page.getSize();

  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 48;
  let y = height - margin;

  // Header
  page.drawText("HIPAA Authorization to Release Medical Records", {
    x: margin,
    y,
    size: 18,
    font: fontBold,
    color: rgb(0.35, 0.17, 0.15),
  });
  y -= 28;

  const subtitle =
    "This authorization allows the release of your medical records to the specified recipient.";
  y = drawWrappedText({
    page,
    text: subtitle,
    x: margin,
    y,
    maxWidth: width - margin * 2,
    lineHeight: 16,
    font: fontRegular,
    size: 11,
  }) - 8;

  // Helper to render labeled field
  const label = (t: string) =>
    page.drawText(t, { x: margin, y, size: 11, font: fontBold, color: rgb(0, 0, 0) });
  const field = (t: string) =>
    drawWrappedText({
      page,
      text: t,
      x: margin,
      y: y - 14,
      maxWidth: width - margin * 2,
      lineHeight: 14,
      font: fontRegular,
      size: 11,
    });

  const v = (k: string) => (values?.[k] !== undefined ? String(values[k]) : "");
  const bool = (k: string) => (values?.[k] ? "Yes" : "No");

  // Patient and request info
  label("Patient Full Name");
  y = field(v("patient_full_name")) - 10;

  label("Date of Birth");
  y = field(v("patient_dob")) - 10;

  label("Provider Name");
  y = field(v("provider_name")) - 10;

  // Provider address block
  const addr1 = v("provider_address_line1");
  const addr2 = v("provider_address_line2");
  const city = v("provider_address_city");
  const state = v("provider_address_state");
  const zip = v("provider_address_zip");
  const lineCityStateZip = [city, state, zip].filter(Boolean).join(", ");
  if (addr1 || addr2 || lineCityStateZip) {
    label("Provider Address");
    y = field(
      [addr1, addr2, lineCityStateZip].filter((s) => s && s.trim().length > 0).join("\n") || "—"
    ) - 10;
  }

  // Provider phone/fax if present
  const phone = v("provider_phone");
  const fax = v("provider_fax");
  if (phone) {
    label("Provider Phone");
    y = field(phone) - 10;
  }
  if (fax) {
    label("Provider Fax");
    y = field(fax) - 10;
  }

  label("Recipient Name/Department");
  y = field(v("recipient_name")) - 10;

  label("Recipient Organization");
  y = field(v("recipient_org")) - 10;

  label("Dates: All Records");
  y = field(bool("dates_all")) - 10;

  label("Information: All Health Information");
  y = field(bool("info_all")) - 10;

  label("Purpose of Disclosure");
  y = field(
    [
      values?.purpose_treatment ? "Treatment" : null,
      values?.purpose_insurance ? "Insurance" : null,
      values?.purpose_legal ? "Legal" : null,
      values?.purpose_personal ? "Personal" : null,
    ]
      .filter(Boolean)
      .join(", ") || "—"
  ) - 10;

  label("Expiration Date");
  y = field(v("expiration_date")) - 10;

  // Acknowledgements
  page.drawText("Acknowledgements", { x: margin, y, size: 12, font: fontBold });
  y -= 16;
  const ackLines = [
    `Revocation acknowledged: ${bool("ack_revocation")}`,
    `Re-disclosure acknowledged: ${bool("ack_redisclosure")}`,
    `Electronic delivery acknowledged: ${bool("ack_electronic_delivery")}`,
    `Conditions acknowledged: ${bool("ack_conditions")}`,
  ];
  for (const line of ackLines) {
    y = drawWrappedText({
      page,
      text: `• ${line}`,
      x: margin,
      y,
      maxWidth: width - margin * 2,
      lineHeight: 14,
      font: fontRegular,
      size: 11,
    }) - 2;
  }
  y -= 8;

  // Signature block
  page.drawText("Signature", { x: margin, y, size: 12, font: fontBold });
  y -= 16;
  // Signature line
  const sigBoxHeight = 80;
  const sigBoxWidth = 280;
  page.drawRectangle({
    x: margin,
    y: y - sigBoxHeight,
    width: sigBoxWidth,
    height: sigBoxHeight,
    borderColor: rgb(0.7, 0.7, 0.7),
    borderWidth: 1,
  });

  if (signatureDataUrl) {
    try {
      const pngBytes = signatureDataUrl.startsWith("data:image/png")
        ? await fetch(signatureDataUrl).then((r) => r.arrayBuffer())
        : undefined;
      if (pngBytes) {
        const png = await pdfDoc.embedPng(pngBytes);
        const scale = Math.min(sigBoxWidth / png.width, sigBoxHeight / png.height) * 0.9;
        const drawW = png.width * scale;
        const drawH = png.height * scale;
        const dx = margin + (sigBoxWidth - drawW) / 2;
        const dy = y - sigBoxHeight + (sigBoxHeight - drawH) / 2;
        page.drawImage(png, { x: dx, y: dy, width: drawW, height: drawH });
      }
    } catch {
      // ignore signature embedding failures
    }
  }

  // Timestamp next to signature
  const signedAtIso = (values?.signature_timestamp as string) || new Date().toISOString();
  const signedAt = new Date(signedAtIso);
  const ts = `Signed on: ${formatLocalDateTime(signedAt)}`;
  page.drawText(ts, { x: margin + sigBoxWidth + 16, y: y - 12, size: 10, font: fontRegular, color: rgb(0.25, 0.25, 0.25) });

  // Footer note
  const footer =
    "By signing, you authorize the release of your medical records as specified above.";
  drawWrappedText({
    page,
    text: footer,
    x: margin,
    y: y - sigBoxHeight - 18,
    maxWidth: width - margin * 2,
    lineHeight: 14,
    font: fontRegular,
    size: 10,
  });

  const bytes = await pdfDoc.save();
  const pdfBuffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  return new Blob([pdfBuffer], { type: "application/pdf" });
}
